import DataType from '../../../../tableEngine/Script/Game/Data/Config/DataStructure';
import TableModel from '../../../../tableEngine/Script/Game/Data/Model/TableModel';
import ITableService from '../../../../tableEngine/Script/Game/Data/Service/ITableService';
import Lobby from '../../../System/Lobby';
import SlotResult, { SpinResult } from '../DataObject/SlotResult';

// 包含 PlayType (下注方式)、Streaming (optional)、Roadmap (紀錄相關 Ex:RTP)
enum LobbyTableEventType {
    HISTORY_UPDATED = 'HISTORY_UPDATED',
    PLAYING_STATE_CHANGED = 'PLAYING_STATE_CHANGED',
    NON_FG_SPIN_TIMES_CHANGED = 'NON_FG_SPIN_TIMES_CHANGED',
    AVERAGE_TO_FG_SPIN_TIMES_CHANGED = 'AVERAGE_TO_FG_SPIN_TIMES_CHANGED',
    GAME_RTP_CHANGED = 'GAME_RTP_CHANGED',
    LIVE_RTP_CHANGED = 'LIVE_RTP_CHANGED',
    IS_FAVORITE_CHANGED = 'IS_FAVORITE_CHANGED',
}

export default class LobbyTableModel extends TableModel {
    public static readonly EVENT_TYPE = { ...LobbyTableEventType, ...TableModel.EVENT_TYPE };

    public readonly history: SpinResult[] = [];
    public readonly hasJP: boolean = false;
    public readonly maxBet: number = 0;
    public readonly minBet: number = 0;
    protected declare _result: SlotResult;
    protected _gameRTP = 0;
    protected _liveRTP = 0;
    protected _nonFGSpinTimes = 0;
    protected _averageToFGSpinTimes = 0;
    private _isFavorite = false;

    constructor(data: DataType.Table.SummaryData, _tableService: ITableService) {
        super(data, _tableService);
        this.maxBet = data.MaximalTotalBet ?? Number.MAX_SAFE_INTEGER;
        this.minBet = data.MinimalTotalBet ?? 0;
    }

    public get result() {
        return this._result;
    }
    protected set result(value: SlotResult) {
        super.result = value;
    }

    public get gameRTP() {
        return this._gameRTP;
    }
    protected set gameRTP(value: number) {
        if (this._gameRTP === value) {
            return;
        }
        this._gameRTP = value;
        this.emit(LobbyTableModel.EVENT_TYPE.GAME_RTP_CHANGED);
    }

    public get liveRTP() {
        return this._liveRTP;
    }
    protected set liveRTP(value: number) {
        if (this._liveRTP === value) {
            return;
        }
        this._liveRTP = value;
        this.emit(LobbyTableModel.EVENT_TYPE.LIVE_RTP_CHANGED);
    }

    public get nonFGSpinTimes() {
        return this._nonFGSpinTimes;
    }
    protected set nonFGSpinTimes(value: number) {
        if (this._nonFGSpinTimes === value) {
            return;
        }
        this._nonFGSpinTimes = value;
        this.emit(LobbyTableModel.EVENT_TYPE.NON_FG_SPIN_TIMES_CHANGED);
    }

    public get averageToFGSpinTimes() {
        return this._averageToFGSpinTimes;
    }
    protected set averageToFGSpinTimes(value: number) {
        if (this._averageToFGSpinTimes === value) {
            return;
        }
        this._averageToFGSpinTimes = value;
        this.emit(LobbyTableModel.EVENT_TYPE.AVERAGE_TO_FG_SPIN_TIMES_CHANGED);
    }

    public get isFavorite() {
        return this._isFavorite;
    }
    private set isFavorite(flag: boolean) {
        if (flag === this._isFavorite) {
            return;
        }
        this._isFavorite = flag;
        this.emit(LobbyTableModel.EVENT_TYPE.IS_FAVORITE_CHANGED);
    }

    public init() {
        super.init();
        // Lobby.connection.on(PathName.PlayerSettle, this.onReceiveResult, this);
        // Lobby.user.on(LobbyUserModel.EVENT_TYPE.FAVORITE_TABLE_LIST_UPDATED, () => {
        //     this.isFavorite = Lobby.user.favoriteTableIDList.includes(this.gameID);
        // });
    }

    public exit(exitManually: boolean, exitTableReason?: string) {
        if (!this._isCurrent) {
            return;
        }
        this._result = null;
        super.exit(exitManually, exitTableReason);
    }

    public updateWithSummaryData(data: DataType.Table.SummaryData) {
        if (this.tableID !== data.TableID) {
            return;
        }
        this.liveRTP = data.LiveRTP === -1 ? 0 : data.LiveRTP;
        this.gameRTP = data.GameRTP;
        super.updateWithSummaryData(data);
    }

    public async requestStatisticData() {
        if (Lobby.hall.duringExitTable) {
            return;
        }
        // const data = await (this.isCurrent
        //     ? Lobby.connection.send(LobbyPathName.TableStatistic)
        //     : Lobby.connection.send(LobbyPathName.TableStatisticQuery, { TableID: this.tableID }));
        // this.updateWithStatisticData(data);
        this.updateWithStatisticData(null);
    }

    protected onStateChanged() {
        super.onStateChanged();
        if (this.state === DataType.Enums.TableState.Betting) {
            this.result = null;
        }
    }

    protected onReceiveResult(data: DataType.Table.PlayerSettleData) {
        if (this.tableID !== data.TableID) {
            return;
        }
        this.result = this._tableService.getResult(data) as SlotResult;
        // this.lastWinNumber = (this.result?.gameWin ?? 0) * Lobby.hall.currentGameData.lobbyToGameDenom;
        this.lastWinNumber = this.result?.gameWin ?? 0;
        this.stopHoldingWin();
    }

    protected updateIdleState() {
        super.updateIdleState();
        if (this.state === DataType.Enums.TableState.Betting) {
            this.result = null;
        }
    }

    protected updateWithStatisticData(data: DataType.Table.StatisticData) {
        if (this.tableID !== data.TableID) {
            return;
        }
        const { NormalCount, FGAverage, RoundCount } = data;
        if (NormalCount !== undefined) {
            this.nonFGSpinTimes = NormalCount;
        }
        if (FGAverage !== undefined) {
            this.averageToFGSpinTimes = FGAverage;
        }
        this._roundCount = RoundCount;
        this.roundID = RoundCount;
        this.onReceiveHistory(data);
    }

    protected onReceiveHistory(data: DataType.Table.StatisticData) {
        const rawHistory = [...data.History];
        // TODO
        // if (data.State === TableState.Showing) {
        //     rawHistory.pop();
        // }
        this.history.length = 0;
        // round count is not updated util next waiting state, so we don't need -1 like rawHistory
        const firstHistoryRoundIndex = data.RoundCount - rawHistory.length;
        this.history.push(...rawHistory.map((resultData, idx) => new SpinResult(resultData, firstHistoryRoundIndex + idx)));
        this.emit(LobbyTableModel.EVENT_TYPE.HISTORY_UPDATED);
    }
}
